import { Box, Tooltip, makeStyles } from '@material-ui/core';
import type { SpaceMember } from '@ably/spaces';
import { getInitials } from 'Utils/getInitials';
import { RfqUserAvatar } from 'pages/auto-rfq/components/user-avatar';
import type { RfqMemberProfileData } from 'lib/realtime/RfqSpacesProvider';

const MAX_VISIBLE = 3;

const useStyles = makeStyles(() => ({
  // Avatar(s) only — no name, no live dot, no colored frame (DEV-412). Sits
  // inline in the card action row, left of the notes button.
  cluster: {
    display: 'inline-flex',
    alignItems: 'center'
  },
  pile: {
    display: 'flex',
    alignItems: 'center'
  },
  item: {
    display: 'inline-flex',
    marginLeft: -6,
    '&:first-child': {
      marginLeft: 0
    }
  },
  // Matches the header PresenceStack avatar: white ring + a blue outline so
  // active collaborators stand out, and a photo avatar with a white background
  // is still delineated against the white card.
  avatar: {
    border: '2px solid #FFFFFF',
    outline: '2px solid #1E96FC',
    outlineOffset: 1,
    fontSize: 11,
    fontWeight: 600,
    boxShadow: '0 1px 2px rgba(0,0,0,0.12)'
  },
  overflow: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    border: '2px solid #FFFFFF',
    outline: '2px solid #1E96FC',
    outlineOffset: 1,
    background: '#5D7083',
    color: '#FFFFFF',
    fontSize: 11,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: -6,
    boxShadow: '0 1px 2px rgba(0,0,0,0.12)'
  }
}));

const profileOf = (member: SpaceMember) =>
  ((member.profileData as unknown) || {}) as RfqMemberProfileData;

const nameOf = (member: SpaceMember) =>
  profileOf(member).name || member.clientId || 'User';

const initialsOf = (member: SpaceMember) => {
  const profile = profileOf(member);
  return (
    getInitials({
      firstName: profile.firstName,
      lastName: profile.lastName,
      email: profile.email
    }) || (nameOf(member).charAt(0) || '?').toUpperCase()
  );
};

/**
 * Compact avatar cluster of the other members currently focused on a single
 * RFQ card (their Spaces location points at this rfqItem). Mirrors the header
 * PresenceStack styling — photo when available, else string-to-color initials.
 */
const Avatar = ({ member }: { member: SpaceMember }) => {
  const profile = profileOf(member);
  const classes = useStyles();
  return (
    <RfqUserAvatar
      size="sm"
      userId={profile.userId}
      value={initialsOf(member)}
      className={classes.avatar}
    />
  );
};

export const RowPresence = ({ members }: { members?: SpaceMember[] }) => {
  const classes = useStyles();

  if (!members?.length) return null;

  // Avatars only; the user's name shows on hover per avatar.
  const visible = members.slice(0, MAX_VISIBLE);
  const overflow = members.length - visible.length;

  return (
    <Box className={classes.cluster}>
      <Box className={classes.pile}>
        {visible.map(member => (
          <Tooltip key={member.connectionId} title={nameOf(member)} arrow>
            <Box component="span" className={classes.item}>
              <Avatar member={member} />
            </Box>
          </Tooltip>
        ))}
        {overflow > 0 && (
          <Tooltip
            title={members
              .slice(MAX_VISIBLE)
              .map(m => nameOf(m))
              .join(', ')}
            arrow>
            <Box className={classes.overflow}>+{overflow}</Box>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};
